import { useState } from "react";
import { ScratchPanel } from "./ScratchPanel";
import type { PremioPublic, SoundStyle } from "@juegossc/shared";

type Props = {
  width: number;
  height: number;
  /** Premio que va a aparecer al raspar (el server ya lo eligió). */
  prize: PremioPublic;
  /** Llamado cuando el cliente raspa lo suficiente. */
  onRevealed?: () => void;
  /** Estilo de sonido — se pasa al ScratchPanel interno. */
  soundStyle?: SoundStyle;
  /** Migration 040: cap de DPR del canvas (nitidez). Se pasa al ScratchPanel. */
  dprMax?: number;
};

// Cantidad de tarjetitas a mostrar. 3 es lo más común en raspaditas
// físicas y entra cómodo en pantallas chicas.
const NUM_TARJETITAS = 3;

// Mecánica "elije tu raspadita":
// 1. Cliente ve N tarjetitas chicas con números/símbolos distintos.
// 2. Toca una → animación de zoom de la elegida → las demás fade out.
// 3. La elegida se transforma en un ScratchPanel grande y se raspa
//    como en la mecánica panel.
// El premio que aparece SIEMPRE es el que eligió el server — la
// elección del cliente es solo teatro (servidor-authoritative).
export function ScratchElije({ width, height, prize, onRevealed, soundStyle, dprMax }: Props) {
  const [chosenIdx, setChosenIdx] = useState<number | null>(null);

  if (chosenIdx === null) {
    // Pantalla de elección — tarjetas en formato apaisado tipo
    // tarjeta de crédito (ratio ~1.6:1 W:H). Más natural para
    // raspaditas reales que el formato vertical.
    const tarjetaW = Math.min(width / NUM_TARJETITAS - 16, 180);
    const tarjetaH = tarjetaW / 1.6;

    return (
      <div className="flex flex-col items-center gap-6" style={{ width }}>
        <h2 className="text-2xl font-bold opacity-90">
          Elegí tu raspadita
        </h2>
        <div className="flex flex-wrap items-center justify-center gap-4">
          {Array.from({ length: NUM_TARJETITAS }).map((_, idx) => (
            <button
              key={idx}
              onClick={() => setChosenIdx(idx)}
              className="group relative overflow-hidden rounded-2xl shadow-2xl transition hover:scale-105 active:scale-95"
              style={{ width: tarjetaW, height: tarjetaH }}
            >
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(135deg, #a07700 0%, #c89a00 50%, #a07700 100%)",
                }}
              />
              {/* Patrón sutil para que se vea raspable */}
              <div
                className="absolute inset-0 opacity-30"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(45deg, rgba(0,0,0,0.1) 0, rgba(0,0,0,0.1) 2px, transparent 2px, transparent 8px)",
                }}
              />
              {/* Número grande */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-4xl font-bold text-[#1a1540] drop-shadow">
                  {idx + 1}
                </span>
              </div>
              {/* Hint que aparece debajo */}
              <div className="absolute inset-x-0 bottom-1.5 text-center text-[10px] font-bold text-[#1a1540] opacity-50">
                Tocá para elegir
              </div>
            </button>
          ))}
        </div>
        <p className="text-sm opacity-60">
          Elegí una raspadita y rascala para descubrir tu premio.
        </p>
      </div>
    );
  }

  // Ya eligió: mostrar el panel grande raspable
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="text-sm opacity-60">Raspadita #{chosenIdx + 1}</div>
      <ScratchPanel width={width} height={height} onRevealed={onRevealed} soundStyle={soundStyle} dprMax={dprMax}>
        <div className="flex flex-col items-center gap-3">
          <div className="text-sm opacity-60">PREMIO</div>
          <div
            className="text-3xl font-bold leading-tight"
            style={{ color: prize.color ?? "#f7c948" }}
          >
            {prize.nombre}
          </div>
        </div>
      </ScratchPanel>
    </div>
  );
}
